import { Box, Button, Checkbox, FormControl, Grid, Typography } from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router';
import DeleteProductHoverIcon from 'assets/icons/detelete-product-hover-icon.svg?react';
import DeleteProductHoverIconWhite from 'assets/icons/detelete-product-icon.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import {
  closeAgreementAtALaterDate,
  closeServiceAgreement,
  fetchAllServiceAgreementsForAccount,
} from 'thunks/accountServiceAgreementsThunk';
import HamburgerIcon from 'assets/icons/hamburger-icon.svg?react';
import CloseIcon from 'assets/icons/close-icon.svg?react';
import CommonDialog from 'components/common/CommonDialog';
import dayjs from 'dayjs';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { jbColors } from 'theme/jbColors';
import { withdrawModificationDataApi } from 'services/service-agreement/withdrawModificationDataApi';
import { setSelectedServiceAgreement } from 'reducers/serviceAgreementsReducer';
import { setLoader } from 'reducers/appConfigReducer';
import { determineStatusFromEnum, StepMetadataType } from 'types';
import { closeModal, openModal } from 'reducers/modalReducer';
import { toUtcCalendarDate } from 'utils/dateUtils';

const datepickerStyle = {
  mt: 1,
  mb: 1,
  mr: 1.3,
  padding: 0,
  '& .MuiInputBase-input': {
    cursor: 'pointer',
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    border: 'none',
    padding: 0,
  },
  '& .MuiInputBase-inputAdornedEnd': {
    border: 'none',
    padding: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiIconButton-edgeEnd': {
    m: 0,
  },
  '& input': {
    cursor: 'pointer',
  },
};

type ProductDetailActionsProps = {
  navigationUrl: string;
  openTask?: () => void;
  brUid?: string | null;
  partnerUid?: string | null;
};

const ProductDetailActions = ({ navigationUrl, openTask, brUid, partnerUid }: ProductDetailActionsProps) => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const [open, setOpen] = React.useState(false);
  const [closureDate, setClosureDate] = React.useState<Date | null>(null);
  const [checked, setChecked] = React.useState<boolean>(false);
  const { selectedServiceAgreement } = useSelector((state: RootState) => state.serviceAgreements);
  const agreementDetailsUrl = brUid
    ? `/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`
    : `/partner/${partnerUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`;

  const dispatch = useDispatch<AppDispatch>();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setClosureDate(null);
  };

  const handleCloseProduct = () => {
    if (checked && closureDate && selectedServiceAgreement?.serviceAgreementId) {
      dispatch(closeAgreementAtALaterDate([selectedServiceAgreement?.serviceAgreementId], closureDate));
    }
    if (selectedServiceAgreement?.serviceAgreementId && !checked && !closureDate) {
      dispatch(closeServiceAgreement([selectedServiceAgreement?.serviceAgreementId]));
    }
    handleCloseDialog();
  };

  const handleChangeLaterClosureDate = (e: { toDate?: () => Date } | null) => {
    setClosureDate(e?.toDate ? (toUtcCalendarDate(e.toDate()) ?? null) : null);
  };

  const handleClick = () => {
    const widthdrawn = async (serviceAgreementId: string) => {
      withdrawModificationDataApi(serviceAgreementId)
        .catch((error) => {
          dispatch(
            openModal({
              modalType: 'JBErrorModalWrapper',
              modalProps: {
                httpError: error,
                okHandler: () => {
                  dispatch(closeModal());
                },
                technicalInfo: {
                  title: 'Error when withdrawing modification',
                  userErrorMessage:
                    'An error occurred when withdrawing modification for Service Agreement. Please try again later.',
                },
              },
            }),
          );
          dispatch(
            setLoader({
              loading: false,
              message: '',
            }),
          );
        })
        .then((res) => {
          if (res?.serviceAgreementId) {
            dispatch(setSelectedServiceAgreement(res));
            dispatch(fetchAllServiceAgreementsForAccount(undefined, brUid ?? undefined, undefined));
            dispatch(
              setLoader({
                loading: false,
                message: '',
              }),
            );
            navigate(agreementDetailsUrl);
          }
        });
    };

    if (selectedServiceAgreement?.agreementStatus !== 'IN_MODIFICATION') {
      navigate(
        selectedServiceAgreement?.canBeModified ? `/accounts/${brUid}/serviceAgreement/modification` : navigationUrl,
      );
    } else {
      dispatch(
        setLoader({
          loading: true,
          message: 'Withdrawing modification...',
        }),
      );
      widthdrawn(selectedServiceAgreement?.serviceAgreementId ?? '');
    }
  };

  const [hover, setHover] = React.useState(false);

  return (
    <>
      <Grid data-testid="product-details-actions" container sx={{ flexDirection: 'row', ml: 0 }}>
        {taskId && (
          <Grid item xs={4}>
            <Button id="formulate-decision-button" onClick={openTask}>
              Formulate Decision
            </Button>
          </Grid>
        )}
        <Grid item xs={taskId ? 4 : 6} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          {selectedServiceAgreement?.canBeClosed && (
            <Button
              onClick={handleOpenDialog}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              id="details-close-product"
              sx={{
                marginRight: '10px',
                backgroundColor: 'white',
                ':hover': {
                  backgroundColor: jbColors.core.reflexBlue[100],
                  color: 'white',
                },
              }}
              data-testid="details-close-product"
            >
              <Box>{hover ? <DeleteProductHoverIconWhite /> : <DeleteProductHoverIcon />} Close Product</Box>
            </Button>
          )}
        </Grid>
        <Grid item xs={taskId ? 4 : 6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            id="details-edit-product"
            data-testid="details-edit-product"
            onClick={handleClick}
            variant="contained"
            disabled={
              selectedServiceAgreement?.agreementStatus === 'IN_MODIFICATION'
                ? !selectedServiceAgreement?.stepMetadata?.find(
                    (s) => s?.stepMetadataType === StepMetadataType.CONFIGURATION_FORM_METADATA,
                  ) && !selectedServiceAgreement?.canWithdrawModification
                : !selectedServiceAgreement?.stepMetadata?.find(
                    (s) => s?.stepMetadataType === StepMetadataType.CONFIGURATION_FORM_METADATA,
                  ) && !selectedServiceAgreement?.canBeModified
            }
          >
            {selectedServiceAgreement?.agreementStatus === 'IN_MODIFICATION' ? 'Withdraw modification' : 'Edit Product'}
          </Button>
        </Grid>
      </Grid>
      <CommonDialog
        onClose={handleCloseDialog}
        isOpen={open}
        title={'Close product'}
        titleIcon={<HamburgerIcon />}
        closeIcon={<CloseIcon onClick={handleCloseDialog} />}
        removeCancel={true}
        content={
          <Grid container>
            <Grid item xs={12} sx={{ mt: 1, mb: 1 }}>
              <Typography variant="h4">Are you sure you want to close the product? This cannot be undone.</Typography>
            </Grid>
            {/*TODO: Uncomment for release 11.2023 */}
            {determineStatusFromEnum(selectedServiceAgreement?.agreementStatus) === 'Active' && (
              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  data-testid="close-product-later-checkbox"
                  value={checked}
                  onChange={() => setChecked(!checked)}
                  sx={{ ml: -1.5 }}
                />
                <Typography variant="body2">I want this product to close on a later date.</Typography>
              </Grid>
            )}
            {determineStatusFromEnum(selectedServiceAgreement?.agreementStatus) === 'Active' && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="body2">The product will close at the end of this day on:</Typography>
              </Grid>
            )}
            {determineStatusFromEnum(selectedServiceAgreement?.agreementStatus) === 'Active' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <MobileDatePicker
                    data-testid="close-product-later-date-picker"
                    disabled={!checked}
                    sx={datepickerStyle}
                    closeOnSelect
                    format="DD.MM.YYYY"
                    slotProps={{
                      actionBar: {
                        actions: [],
                      },
                    }}
                    disablePast
                    defaultValue={closureDate && dayjs(new Date(closureDate))}
                    value={closureDate ? dayjs(new Date(closureDate)) : undefined}
                    onChange={(e) => handleChangeLaterClosureDate(e)}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sx={{ mt: 4 }}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  id="product-detail-cancel"
                  variant="contained"
                  color="primary"
                  onClick={handleCloseDialog}
                  sx={{
                    marginRight: '10px',
                    color: jbColors.core.reflexBlue[100],
                    backgroundColor: 'white',
                    border: `1px solid ${jbColors.core.reflexBlue[100]}`,
                    '&:hover': {
                      backgroundColor: jbColors.core.reflexBlue[100],
                      color: 'white',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="close-product-button"
                  variant="contained"
                  color="primary"
                  disabled={!selectedServiceAgreement?.canBeClosed}
                  onClick={handleCloseProduct}
                >
                  Close Product
                </Button>
              </Box>
            </Grid>
          </Grid>
        }
      />
    </>
  );
};

export default ProductDetailActions;
